import { Button } from '@sunwebgroup/uds-ui/button'
import * as Collapsible from '@sunwebgroup/uds-ui/collapsible'
import { Children, isValidElement, type PropsWithChildren, useEffect, useMemo, useState } from 'react'
import styles from './ShowMore.module.css'
import { splitAtNth } from './splitAtNth'

type ShowMoreProps = PropsWithChildren<{
  className?: string
  closeLabel: string
  minimumHiddenItems?: number
  openLabel: string
  visibleItems?: number
  onOpen?: (value: boolean) => void
}>

export function ShowMore({
  children,
  className,
  closeLabel,
  minimumHiddenItems = 2,
  openLabel,
  visibleItems = 3,
  onOpen,
}: ShowMoreProps) {
  const [open, setOpen] = useState(false)

  const lastCheckedIndex = Children.toArray(children)
    .map(child => isValidElement<{ checked?: boolean }>(child) && child.props.checked)
    .lastIndexOf(true)
  const isHiddenChildChecked = lastCheckedIndex >= visibleItems

  const [shownChildren, hiddenChildren] = useMemo(
    () => splitAtNth(Children.toArray(children), visibleItems),
    [children, visibleItems],
  )

  const hiddenChildrenCount = Children.count(hiddenChildren)
  const shouldHideChildren = hiddenChildrenCount >= minimumHiddenItems

  const handleOpenChange = (value: boolean) => {
    setOpen(prev => !prev)
    onOpen?.(value)
  }

  useEffect(() => {
    if (!shouldHideChildren) {
      setOpen(false)
    }
  }, [shouldHideChildren])

  useEffect(() => {
    if (isHiddenChildChecked && !open) {
      setOpen(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Disabled because we only want to listen to changes in 'isHiddenChildChecked' and not 'open'
  }, [isHiddenChildChecked])

  return (
    <div className={className}>
      {shownChildren}
      {!shouldHideChildren && hiddenChildren}
      {shouldHideChildren && hiddenChildren && (
        <Collapsible.Root onOpenChange={handleOpenChange} defaultOpen={isHiddenChildChecked}>
          <Collapsible.Content>{hiddenChildren}</Collapsible.Content>
          <Collapsible.Trigger asChild>
            <Button variant="transparentNeutral" className={styles.showMoreButton}>
              {open ? closeLabel : openLabel}
              <Button.Icon glyph={open ? 'ChevronUpExtraSmall' : 'ChevronDownExtraSmall'} />
            </Button>
          </Collapsible.Trigger>
        </Collapsible.Root>
      )}
    </div>
  )
}
